import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { qualityInspectionSchema, type QualityInspectionFormData } from "@/schemas/qualitySchemas";

interface QualityInspectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId?: string;
}

export const QualityInspectionDialog = ({ open, onOpenChange, batchId }: QualityInspectionDialogProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inspectionImages, setInspectionImages] = useState<string[]>([]);

  // Los lotes reales viven en production_lots (módulo Lotes); production_batches
  // es el concepto antiguo y está vacío — por eso el desplegable salía incompleto.
  const { data: lots } = useQuery({
    queryKey: ["production-lots-quality"],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_lots")
        .select("id, lot_number, name, status")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const form = useForm<QualityInspectionFormData>({
    resolver: zodResolver(qualityInspectionSchema),
    defaultValues: {
      production_batch_id: batchId || "",
      inspection_type: "in_process",
      visual_check_passed: false,
      dimensional_check_passed: false,
      functional_check_passed: false,
      sample_size: 1,
      overall_rating: 3,
      notes: "",
    },
  });

  const createInspection = useMutation({
    mutationFn: async (data: QualityInspectionFormData) => {
      const user = getCurrentUser();
      if (!user) throw new Error("No autorizado");

      // Mapeo al esquema vivo de quality_inspections (el anterior usaba columnas
      // que ya no existen y el insert fallaba siempre). El campo del formulario
      // production_batch_id contiene ahora el id del lote (production_lots).
      const inspectionData = {
        production_lot_id: data.production_batch_id,
        inspector_id: user.id,
        inspection_type: data.inspection_type,
        status: "in_progress" as const,
        visual_inspection: data.visual_check_passed,
        dimensional_inspection: data.dimensional_check_passed,
        mechanical_test: data.functional_check_passed,
        samples_inspected: data.sample_size,
        surface_quality: data.overall_rating != null ? String(data.overall_rating) : null,
        notes: data.notes,
      };

      const { data: insertedData, error } = await api
        .from("quality_inspections")
        .insert([inspectionData])
        .select()
        .single();

      if (error) throw error;

      // Si hay imágenes, también guardarlas en la galería para referencia
      if (inspectionImages.length > 0 && insertedData) {
        for (const imageUrl of inspectionImages) {
          await api.from("gallery").insert([{
            element_id: insertedData.id,
            element_type: "quality_inspection",
            image_url: imageUrl,
            description: `Inspección ${data.inspection_type} - ${new Date().toLocaleDateString()}`,
            uploaded_by: user.id,
          }]);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quality-inspections"] });
      toast.success("Inspección de calidad creada exitosamente");
      form.reset();
      setInspectionImages([]);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Error al crear la inspección: " + error.message);
    },
  });

  const onSubmit = async (data: QualityInspectionFormData) => {
    setIsSubmitting(true);
    try {
      await createInspection.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva Inspección de Calidad</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="production_batch_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lote de Producción *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un lote" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {lots?.map((lot) => (
                        <SelectItem key={lot.id} value={lot.id}>
                          {lot.lot_number}{lot.name ? ` - ${lot.name}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inspection_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Inspección *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona el tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="incoming">Recepción</SelectItem>
                      <SelectItem value="in_process">En Proceso</SelectItem>
                      <SelectItem value="final">Final</SelectItem>
                      <SelectItem value="random">Aleatoria</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sample_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tamaño de Muestra</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="overall_rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Calificación (1-5)</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" max="5" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 border rounded-lg p-4">
              <h3 className="font-semibold">Verificaciones</h3>
              
              <FormField
                control={form.control}
                name="visual_check_passed"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>Inspección Visual</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dimensional_check_passed"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>Inspección Dimensional</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="functional_check_passed"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>Prueba Funcional</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} placeholder="Observaciones adicionales..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sección de Imágenes de la Inspección */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                📷 Evidencia Fotográfica
              </h3>
              <p className="text-sm text-muted-foreground">
                Sube fotos de la inspección, defectos encontrados o cualquier evidencia relevante.
              </p>
              <MultiImageUpload
                value={inspectionImages}
                onChange={setInspectionImages}
                maxImages={5}
                maxSizeMB={3}
                label=""
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Creando..." : "Crear Inspección"}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
